import { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Table, Row, Col, Form, Button, Modal } from 'react-bootstrap';

import InsightsLoader from 'components/loaders/InsightsLoader';
import { piiSharingActions } from 'pages/integrations/containers/LtiIntegrationContainer/piiSharing/actions';

const PiiSharingSection = ({ piiSharingConfigs, isLoading, updatePiiSharingConfig, addPiiSharingCourse }) => {
  const [newCourseKey, setNewCourseKey] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  // Holds the action awaiting confirmation, either:
  // { type: 'add', courseKey } or { type: 'toggle', courseId, displayName, nextEnabled }.
  const [pendingAction, setPendingAction] = useState(null);

  const trimmedKey = newCourseKey.trim();
  // We already hold the tenant's flagged courses, so block re-adding one client-side
  // instead of round-tripping to the API only to create a duplicate flag row.
  const isDuplicate = piiSharingConfigs.some((course) => course.course_id === trimmedKey);

  const handleAddCourse = () => {
    if (!trimmedKey || isDuplicate) {
      return;
    }
    setPendingAction({ type: 'add', courseKey: trimmedKey });
  };

  const confirmAction = () => {
    if (pendingAction?.type === 'add') {
      setIsAdding(true);
      addPiiSharingCourse(pendingAction.courseKey)
        .then(() => setNewCourseKey(''))
        .catch(() => {
          // Error toast already shown by the addPiiSharingCourse action.
        })
        .finally(() => setIsAdding(false));
    } else if (pendingAction?.type === 'toggle') {
      updatePiiSharingConfig(pendingAction.courseId, pendingAction.nextEnabled);
    }
    setPendingAction(null);
  };

  return (
    <div className="integration-holder mt-4">
      <div className="subheader-holder">
        <h5>PII Sharing</h5>
        <p className="m-0">
          Allow LTI Launch to send the learner&apos;s Personally Identifiable Information (PII), including username,
          full name, and email, to the LTI tool.
        </p>
        <p className="m-0 small">Required for tools like Turnitin to identify submissions.</p>
        <p className="text-muted small">
          After enabling, the course author must still turn on &quot;Request user&apos;s username / full name /
          email&quot; on each LTI component in Studio for this to take effect.
        </p>
      </div>
      <Row className="align-items-center mb-3">
        <Col xs="12" lg="5">
          <Form.Group>
            <Form.Control
              type="text"
              placeholder="e.g., course-v1:edX+DemoX+Demo_Course"
              value={newCourseKey}
              disabled={isAdding}
              isInvalid={isDuplicate}
              onChange={(event) => setNewCourseKey(event.target.value)}
            />
            {isDuplicate && <Form.Text className="text-danger">This course is already in the list below.</Form.Text>}
          </Form.Group>
        </Col>
        <Col xs="12" lg="7" className="d-flex justify-content-end mt-3 mt-lg-0">
          <Button onClick={handleAddCourse} disabled={isAdding || !trimmedKey || isDuplicate}>
            {isAdding && <span className="spinner-border spinner-border-sm me-2" />}
            Enable PII Sharing
          </Button>
        </Col>
      </Row>
      {isLoading ? (
        <InsightsLoader />
      ) : (
        <div className="table-holder">
          <Table className="dns-table align-middle">
            <thead>
              <tr>
                <th>Course</th>
                <th>PII Sharing Enabled</th>
              </tr>
            </thead>
            <tbody>
              {piiSharingConfigs.map((course) => (
                <tr key={course.course_id}>
                  <td data-title="Course">
                    <strong>{course.display_name}</strong>
                    <div className="text-muted small">{course.course_id}</div>
                  </td>
                  <td data-title="PII Sharing Enabled">
                    <input
                      type="checkbox"
                      checked={course.enabled}
                      className="checkbox"
                      onChange={() =>
                        setPendingAction({
                          type: 'toggle',
                          courseId: course.course_id,
                          displayName: course.display_name,
                          nextEnabled: !course.enabled
                        })
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      <Modal onHide={() => setPendingAction(null)} show={!!pendingAction} backdrop="static" size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            {pendingAction?.nextEnabled === false ? 'Disable PII sharing?' : 'Enable PII sharing?'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ minHeight: '10px' }}>
          {pendingAction?.nextEnabled === false ? (
            <>
              The LTI tool will no longer receive learner username, full name, or email in{' '}
              <strong>{pendingAction?.displayName}</strong>. Continue?
            </>
          ) : (
            <>
              Learner username, full name, and email will be sent to the LTI tool on every launch in{' '}
              <strong>{pendingAction?.displayName || pendingAction?.courseKey}</strong>. Continue?
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPendingAction(null)}>
            Cancel
          </Button>
          <Button variant={pendingAction?.nextEnabled === false ? 'danger' : 'primary'} onClick={confirmAction}>
            {pendingAction?.nextEnabled === false ? 'Yes, disable' : 'Yes, enable'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

function mapStateToProps(state) {
  const { piiSharingConfigs, isLoading } = state.PII_SHARING_CONFIGS;
  return {
    piiSharingConfigs,
    isLoading
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(piiSharingActions, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PiiSharingSection);
